import { getBrandPath } from "../contexts/ConfigContext/constants";

const STALL_PATHS = ["/wang", "/pdd", "/kokoro", "/shabudays", "/flint", "/torikizoku"];
const PATH_UUID_PATTERN = /^\/uuid=([a-zA-Z0-9-]+)/;

const getViteBaseUrl = (): string => import.meta.env.BASE_URL || "/";

const redirectPathBasedUuid = (pathUuid: string, viteBaseUrl: string, search: string): void => {
	const newSearchParams = new URLSearchParams(search);

	newSearchParams.set("uuid", pathUuid);

	const qs = newSearchParams.toString();

	if (viteBaseUrl === "/") {
		// Same-origin redirect: query string is sanitised through URLSearchParams.
		globalThis.location.replace(`/?${qs}`); // NOSONAR tssecurity:S6105

		return;
	}

	// Same-origin redirect: base from Vite, query sanitised through URLSearchParams.
	const base = viteBaseUrl.endsWith("/") ? viteBaseUrl.slice(0, -1) : viteBaseUrl;

	globalThis.location.replace(`${base}/index.html?${qs}`); // NOSONAR tssecurity:S6105
};

const redirectToBrandPath = (brandPath: string, search: string): void => {
	// brandPath is from getBrandPath()'s closed allowlist; redirect stays same-origin.
	globalThis.location.replace(`${brandPath}${search}`); // NOSONAR tssecurity:S6105
};

const findStallBasePath = (pathname: string): string | null => {
	for (const stallPath of STALL_PATHS) {
		if (pathname.includes(stallPath)) {
			const baseIndex = pathname.indexOf(stallPath);

			return pathname.substring(0, baseIndex) + stallPath;
		}
	}

	return null;
};

/**
 * The router basename a pathname belongs to, e.g. "/shabudays/payment" -> "/shabudays".
 * Falls back to the build's base for anything that is not a stall path.
 */
export const getBaseUrlForPath = (pathname: string): string =>
	findStallBasePath(pathname) ?? getViteBaseUrl();

// Dynamic base URL detection for multi-stall support
const getPublicBaseUrl = (): string => {
	if (typeof globalThis === "undefined" || !globalThis.location) {
		return getViteBaseUrl();
	}

	const { pathname, search } = globalThis.location;
	const urlParams = new URLSearchParams(search);
	const uuid = urlParams.get("uuid");

	// Handle path-based UUID format: /uuid=<uuid>
	// Some QR codes encode uuid in the path instead of query string.
	const pathUuidMatch = PATH_UUID_PATTERN.exec(pathname);

	if (pathUuidMatch) {
		const viteBaseUrl = getViteBaseUrl();

		redirectPathBasedUuid(pathUuidMatch[1], viteBaseUrl, search);

		return viteBaseUrl;
	}

	// Handle UUID-based auto-redirect to brand base path at root.
	// e.g. /?uuid=<torikizokuUUID> -> /torikizoku?uuid=<...>
	if (pathname === "/" && uuid) {
		const brandPath = getBrandPath(uuid);

		if (brandPath) {
			redirectToBrandPath(brandPath, search);

			return brandPath;
		}
	}

	// Check for existing multi-stall paths
	const stallBase = findStallBasePath(pathname);

	if (stallBase) {
		return stallBase;
	}

	return getViteBaseUrl();
};

export const publicBaseUrl = getPublicBaseUrl();
/** The build's own base path ("/", or "/develop/" on sub-path deploys) — never a stall path. */
export const VITE_BASE_URL = getViteBaseUrl();
export const ADYEN_ENV = import.meta.env.VITE_ADYEN_ENV || "test";
export const GIT_COMMIT = import.meta.env.VITE_GIT_COMMIT;

// Version Display related constants
export const APP_VERSION = import.meta.env.VITE_APP_VERSION;
export const GIT_COMMIT_HASH = import.meta.env.VITE_GIT_COMMIT;
export const APP_MODE = import.meta.env.MODE || "development";
export const DD_ENV = import.meta.env.VITE_DD_ENV;

// Auth0 / Linkpass
export const LINKPAY_AUTH0_DOMAIN = import.meta.env.VITE_LINKPAY_AUTH0_DOMAIN as string | undefined;
export const LINKPAY_AUTH0_CLIENT_ID = import.meta.env.VITE_LINKPAY_AUTH0_CLIENT_ID as string | undefined;
// Where LinkPass sends the customer back. Their setting, not ours: the app only reports it
// back to them, so it stays configurable per tenant rather than fixed in the code.
export const LINKPAY_AUTH0_REDIRECT_URI = import.meta.env.VITE_LINKPAY_AUTH0_REDIRECT_URI as string | undefined;
export const LINKPAY_AUTH0_AUDIENCE = import.meta.env.VITE_LINKPAY_AUTH0_AUDIENCE as string | undefined;
/**
 * Which gateway the payment SDK talks to. Only the literal "production" selects production —
 * anything else, including unset, resolves to preproduction, so a build missing the variable
 * fetches no member's real cards.
 */
export const PAYMENT_SDK_ENV = import.meta.env.VITE_PAYMENT_SDK_ENV as string | undefined;
export const LINKPAY_AUTH0_SCOPE = import.meta.env.VITE_LINKPAY_AUTH0_SCOPE as string | undefined;
